import {
  TICKET_EVENT_TYPE_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_STATE_LABELS,
  UNTRIAGED_LABEL,
  threadEntryAnchor,
  type TicketAssignmentEventDetails,
  type TicketEventEntry,
} from '@easyhelp/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { formatDateTime } from '../../components/ticket';
import { ESCALATION_LABEL, notificationText, roleAndLevel } from './assignment-text';
import { attachmentEventText } from './attachment-text';
import { DeliveryChip } from './DeliveryChip';
import { eventActor } from './event-actor';
import { WithViaMcpAbbr } from './ViaMcp';
import { receiptConfirmationText } from './receipt-confirmation-text';
import { correctionEventNote, stateChangeReason } from './state-event-text';
import { teamNotificationText } from './team-notification-text';
import { THREAD_ENTRY_ATTRIBUTE, THREAD_TARGET_SX } from './thread-anchor';

/**
 * Resa di un evento di sistema (#120): una riga sottile, centrata e in corsivo, senza card, come gli
 * eventi di sistema del linguaggio visivo di EasyCare (AUDIT-001 § Thread e composer). La stessa
 * riga vale per un evento isolato e per gli eventi di un gruppo aperto.
 */

/** Una riga di testo dell'evento: didascalia in corsivo, attenuata, che va a capo. */
function EventText({ children, color = 'text.secondary' }: { children: ReactNode; color?: string }) {
  return (
    <Typography variant="caption" component="p" sx={{ m: 0, fontStyle: 'italic', color, overflowWrap: 'anywhere' }}>
      <WithViaMcpAbbr>{children}</WithViaMcpAbbr>
    </Typography>
  );
}

/** «Urgente (era Da triare)»: il livello nuovo e quello di prima, per un cambio di priorità (#3). */
function priorityChangeText({ from, to }: NonNullable<TicketEventEntry['priorityChange']>): string {
  return `${TICKET_PRIORITY_LABELS[to]} (era ${from === null ? UNTRIAGED_LABEL : TICKET_PRIORITY_LABELS[from]})`;
}

function stateChangeText({ from, to }: NonNullable<TicketEventEntry['stateChange']>): string {
  return `«${TICKET_STATE_LABELS[to]}» (era «${TICKET_STATE_LABELS[from]}»)`;
}

/**
 * Cosa la ricezione ha scartato per la regola «riferimento al record consentito» del committente
 * (easycare DEC-101, DEC-014): mai il valore, che non è stato conservato.
 */
function discardedNote(entry: TicketEventEntry): string {
  const discarded = [
    ...(entry.recordReferenceDiscarded ? ['il riferimento al record'] : []),
    ...(entry.patientIdentifierDiscarded ? ["l'identificativo paziente"] : []),
  ];
  if (discarded.length === 0) return '';
  const subject = discarded.join(' e ');
  const what = subject.charAt(0).toUpperCase() + subject.slice(1);
  return discarded.length > 1
    ? ` ${what} inviati non sono stati conservati: la regola del committente non li consente.`
    : ` ${what} inviato non è stato conservato: la regola del committente non lo consente.`;
}

/** La frase di un evento che non è un'assegnazione. */
function eventSentence(entry: TicketEventEntry): string {
  const label = `${TICKET_EVENT_TYPE_LABELS[entry.type]} da ${eventActor(entry)}`;
  // L'esito di una notifica interna al team (#70) non è un'email al segnalante.
  const text =
    teamNotificationText(entry) ??
    (entry.receiptConfirmation ? receiptConfirmationText(entry.receiptConfirmation) : null) ??
    attachmentEventText(entry) ??
    (entry.priorityChange
      ? `${label}: ${priorityChangeText(entry.priorityChange)}.`
      : entry.stateChange
        ? `${label}: ${stateChangeText(entry.stateChange)}.${stateChangeReason(entry.stateChange)}`
        : `${label}.${correctionEventNote(entry)}`);
  return `${text}${discardedNote(entry)}`;
}

/**
 * Evento di assegnazione (story #4): chi ha agito, il membro con ruolo e livello, il responsabile
 * precedente nel cambio, l'escalation al secondo livello e l'esito della notifica all'indirizzo
 * del collega. Mai il testo della segnalazione: qui non c'è.
 */
function AssignmentEventLines({
  entry,
  assignment,
}: {
  entry: TicketEventEntry;
  assignment: TicketAssignmentEventDetails;
}) {
  const { member, previous, escalation, notification } = assignment;
  const instead = previous ? `, al posto di ${previous.name}` : '';
  return (
    <>
      <EventText>
        {`${formatDateTime(entry.at)} · ${TICKET_EVENT_TYPE_LABELS[entry.type]} da ${eventActor(entry)}: ${member.name} (${roleAndLevel(member)})${instead}.`}
      </EventText>
      {escalation && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          <Chip size="small" color="warning" variant="outlined" label={ESCALATION_LABEL} />
          <EventText>Il ticket passa al secondo livello.</EventText>
        </Stack>
      )}
      <EventText color={notification?.outcome === 'non_inviata' ? 'warning.dark' : 'text.secondary'}>
        {notification ? notificationText(notification) : 'Nessuna notifica.'}
      </EventText>
    </>
  );
}

/** Un evento di sistema come `li` con l'ancora del link delle notifiche (`e-<id>`, #70). */
export function ThreadEvent({ entry }: { entry: TicketEventEntry }) {
  return (
    <Box
      component="li"
      id={threadEntryAnchor(entry)}
      // Focalizzabile solo da script: aprendo il link di una notifica il focus arriva qui (#70).
      tabIndex={-1}
      {...{ [THREAD_ENTRY_ATTRIBUTE]: '' }}
      data-variant="evento"
      sx={{ listStyle: 'none', px: 2, py: 0.5, borderRadius: 1, textAlign: 'center', ...THREAD_TARGET_SX }}
    >
      <Stack spacing={0.5} alignItems="center">
        {entry.assignment ? (
          <AssignmentEventLines entry={entry} assignment={entry.assignment} />
        ) : (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            alignItems="center"
            justifyContent="center"
            sx={{ flexWrap: 'wrap' }}
          >
            <EventText>
              {formatDateTime(entry.at)} · {eventSentence(entry)}
            </EventText>
            {/* La conferma di ricezione (#117) ha il suo stato di consegna, come le risposte. */}
            {entry.receiptConfirmation && <DeliveryChip delivery={entry.receiptConfirmation.delivery} />}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
